const STAR_PATH = 'M8 1.5l1.85 3.9 4.15.55-3.05 3 .75 4.3L8 11.2l-3.7 2.05.75-4.3-3.05-3 4.15-.55L8 1.5z';
const PUBLISHED_VALUES = ['', 'true', 'yes', '1'];

// Google serves the published sheet with `cache-control: no-store`, so the
// browser is not allowed to reuse it. Cache it per session ourselves instead:
// without this, every product page pays the same ~0.5-1s round trip again.
// 5 minutes matches how long publish-to-web caches server-side anyway, so this
// does not make the data measurably staler than it already is.
const CACHE_PREFIX = 'testi-sheet:';
const CACHE_TTL_MS = 5 * 60 * 1000;

// Shared between wrappers so two sections on one page make one request.
const inFlight = new Map();

function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && next === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}

function rowsToObjects(rows) {
  if (!rows.length) return [];

  const headers = rows[0].map((header) => header.trim().toLowerCase().replace(/\s+/g, '_'));

  return rows
    .slice(1)
    .map((row) => Object.fromEntries(headers.map((key, i) => [key, (row[i] || '').trim()])));
}

function isPublished(row) {
  if (!('published' in row) && !('show' in row) && !('visible' in row)) return true;
  const value = (row.published || row.show || row.visible || '').toLowerCase();
  return PUBLISHED_VALUES.includes(value);
}

// "handle-a | handle-b" -> ['handle-a', 'handle-b']
function parseHandles(value) {
  return (value || '')
    .split('|')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function matchesProduct(row, productHandle) {
  const column = row.product_handles || row.product_handle || row.handle || '';
  return parseHandles(column).includes(productHandle);
}

function reviewsFor(rows, productHandle) {
  return rows.filter((row) => isPublished(row) && matchesProduct(row, productHandle));
}

// Lowest position first; rows with no position fall to the end in sheet order.
function positionOf(row) {
  const raw = row.position !== undefined && row.position !== '' ? row.position : row.sort_order;
  const value = Number.parseFloat(raw);
  return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value;
}

function readCache(sheetUrl) {
  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + sheetUrl);
    if (!cached) return null;

    const { csv, storedAt } = JSON.parse(cached);
    if (typeof csv !== 'string' || Date.now() - storedAt > CACHE_TTL_MS) return null;

    return csv;
  } catch {
    return null; // private browsing, disabled storage, or malformed entry
  }
}

function writeCache(sheetUrl, csv) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + sheetUrl, JSON.stringify({ csv, storedAt: Date.now() }));
  } catch {
    // Caching is an optimisation - a storage failure must not break rendering.
  }
}

async function requestSheet(sheetUrl) {
  const response = await fetch(sheetUrl);
  if (!response.ok) throw new Error(`Failed to fetch sheet: ${response.status}`);

  const csv = await response.text();
  writeCache(sheetUrl, csv);
  return csv;
}

function fetchSheet(sheetUrl) {
  const cached = readCache(sheetUrl);
  if (cached !== null) return Promise.resolve(cached);

  if (!inFlight.has(sheetUrl)) {
    inFlight.set(
      sheetUrl,
      requestSheet(sheetUrl).finally(() => inFlight.delete(sheetUrl))
    );
  }

  return inFlight.get(sheetUrl);
}

function starIcon(filled) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('class', `testi-star${filled ? ' is-filled' : ''}`);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', STAR_PATH);
  path.setAttribute('fill', 'currentColor');

  svg.appendChild(path);
  return svg;
}

function ratingElement(rating) {
  const value = Math.max(0, Math.min(5, Math.round(Number.parseFloat(rating) || 0)));
  const wrap = document.createElement('div');
  wrap.className = 'testi-rating';
  wrap.setAttribute('aria-label', `${value} out of 5 stars`);

  for (let star = 1; star <= 5; star++) {
    wrap.appendChild(starIcon(star <= value));
  }

  return wrap;
}

function textSpan(className, text) {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = text;
  return span;
}

function fieldsOf(row) {
  return {
    quote: row.review || row.quote || row.text || '',
    name: row.reviewer_name || row.name || row.reviewer || '',
    city: row.city || row.city_name || row.location || '',
    rating: row.rating || row.stars || row.ratings || '',
  };
}

function personElement(name, city) {
  const personText = document.createElement('span');
  personText.className = 'testi-person-text';
  if (name) personText.appendChild(textSpan('testi-name', name));
  if (city) personText.appendChild(textSpan('testi-subtitle', city));

  const person = document.createElement('div');
  person.className = 'testi-person';
  person.appendChild(personText);
  return person;
}

// Name, city and rating travel together so CSS can pin them to the card bottom.
function footerElement({ name, city, rating }) {
  const footer = document.createElement('div');
  footer.className = 'testi-footer';
  footer.appendChild(personElement(name, city));
  if (rating !== '') footer.appendChild(ratingElement(rating));
  return footer;
}

// One dialog is shared by every card on the page.
let reviewDialog = null;

function buildDialog() {
  const dialog = document.createElement('dialog');
  dialog.className = 'testi-dialog';

  const body = document.createElement('div');
  body.className = 'testi-dialog-body';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'testi-dialog-close';
  close.setAttribute('aria-label', 'Close review');
  close.innerHTML = '&times;';
  close.addEventListener('click', () => dialog.close());

  body.appendChild(close);
  dialog.appendChild(body);

  // Clicking the backdrop (the dialog element itself) closes it; Escape is native.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  document.body.appendChild(dialog);
  return dialog;
}

function openReview(row) {
  if (!reviewDialog || !reviewDialog.isConnected) reviewDialog = buildDialog();

  const body = reviewDialog.querySelector('.testi-dialog-body');
  body.querySelectorAll(':scope > :not(.testi-dialog-close)').forEach((node) => node.remove());

  const fields = fieldsOf(row);

  const quoteEl = document.createElement('p');
  quoteEl.className = 'testi-dialog-quote';
  quoteEl.textContent = fields.quote;
  body.appendChild(quoteEl);
  body.appendChild(footerElement(fields));

  reviewDialog.showModal();
}

function cardElement(row) {
  const fields = fieldsOf(row);

  const card = document.createElement('li');
  card.className = 'testi-card';

  // The slide element is not safe to lay out on: `.is-visible` in app.css sets
  // `display: block !important` on it, so any grid/flex declared there loses.
  // Everything goes in an inner wrapper that nothing else touches.
  const body = document.createElement('div');
  body.className = 'testi-card-body';
  card.appendChild(body);

  if (fields.quote) {
    const quoteEl = document.createElement('p');
    quoteEl.className = 'testi-quote';
    quoteEl.textContent = fields.quote;
    body.appendChild(quoteEl);

    // Revealed after layout, only when the text is actually clamped.
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'testi-readmore';
    more.textContent = 'Read more';
    more.hidden = true;
    more.addEventListener('click', () => openReview(row));
    body.appendChild(more);
  }

  body.appendChild(footerElement(fields));

  return card;
}

// The clamp is a CSS line-clamp, so whether it truncated is only knowable after
// layout - hence this runs once the wrapper is visible.
function revealReadMore(list) {
  list.querySelectorAll('.testi-card').forEach((card) => {
    const quote = card.querySelector('.testi-quote');
    const button = card.querySelector('.testi-readmore');
    if (!quote || !button) return;
    if (quote.scrollHeight > quote.clientHeight + 1) button.hidden = false;
  });
}

function initSplide(wrapper, count) {
  const track = wrapper.querySelector('.testi-track');
  const list = wrapper.querySelector('.testi-list');
  const controls = wrapper.querySelector('.testi-controls');

  if (count <= 1 || !track || !list) return;

  if (controls) controls.hidden = false;

  wrapper.classList.add('splide');
  track.classList.add('splide__track');
  list.classList.add('splide__list');
  wrapper.querySelectorAll('.testi-card').forEach((card) => card.classList.add('splide__slide'));

  const splide = new Splide(wrapper, {
    type: 'slide',
    perMove: 1,
    autoWidth: true,
    gap: '1.5rem',
    arrows: false,
    pagination: false,
    drag: true,
    updateOnMove: true,
  });

  const fill = wrapper.querySelector('.testi-progress-fill');
  const prevBtn = wrapper.querySelector('.testi-prev');
  const nextBtn = wrapper.querySelector('.testi-next');

  function updateProgress() {
    if (!fill) return;
    fill.style.width = `${((splide.index + 1) / count) * 100}%`;
  }

  splide.on('mounted move', updateProgress);
  prevBtn?.addEventListener('click', () => splide.go('<'));
  nextBtn?.addEventListener('click', () => splide.go('>'));

  splide.mount();
}

// This product's rows, or - when it has none - those of the first product in
// custom.product_linked that does have rows. One fetch covers both.
function resolveMatches(rows, productHandle, linkedHandles) {
  const own = reviewsFor(rows, productHandle);
  if (own.length) return own;

  for (const handle of linkedHandles) {
    if (handle === productHandle) continue;
    const borrowed = reviewsFor(rows, handle);
    if (borrowed.length) return borrowed;
  }

  return [];
}

async function loadWrapper(wrapper) {
  if (wrapper.dataset.testiInit) return;
  wrapper.dataset.testiInit = 'true';

  const { sheetUrl, linkedProductHandles } = wrapper.dataset;
  const productHandle = (wrapper.dataset.productHandle || '').trim().toLowerCase();
  if (!sheetUrl || !productHandle) return;

  const linkedHandles = parseHandles(linkedProductHandles);

  try {
    const rows = rowsToObjects(parseCSV(await fetchSheet(sheetUrl)));
    const matches = resolveMatches(rows, productHandle, linkedHandles);
    if (!matches.length) return;

    matches.sort((a, b) => positionOf(a) - positionOf(b));

    const list = wrapper.querySelector('.testi-list');
    matches.forEach((row) => list.appendChild(cardElement(row)));

    wrapper.dataset.count = matches.length;
    wrapper.hidden = false;
    revealReadMore(list);
    initSplide(wrapper, matches.length);
  } catch (error) {
    console.warn('Product testimonials: could not load Google Sheet data.', error);
  }
}

function init() {
  document.querySelectorAll('.testi-wrapper[data-sheet-url]').forEach(loadWrapper);
}

document.addEventListener('shopify:section:load', init);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
